/**
 * Persist UTM (and related attribution) query params in sessionStorage so they
 * remain available for form submissions even after the user navigates away
 * from the original landing URL (e.g. clicked an email link, then browsed to
 * another page before filling out a form).
 *
 * sessionStorage is used so attribution is scoped to a single browser tab /
 * visit — matching typical Pardot / GA session semantics. Switch to
 * localStorage if cross-session attribution is required.
 *
 * If the landing URL has no UTMs, values are derived from document.referrer so
 * that direct, organic-search, and social visits still submit attribution
 * (utm_source=direct/utm_medium=none for direct, referrer hostname + medium
 * = organic|social|referral otherwise). Explicit UTMs in the URL always win.
 */

const STORAGE_KEY = 'credera_utm_params';

const TRACKED_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'web_region_source'];

// Referrer hostname classification. Entries with `prefix` match any host
// starting with that string followed by `.` (covers ccTLDs like google.co.uk).
// Entries with `exact` require a full hostname match (used for short/ambiguous
// domains such as t.co, x.com, fb.com that would otherwise substring-match
// unrelated hosts like reddit.com or abbot.com).
const REFERRER_SOURCE_MAP = [
  { prefix: 'google', source: 'google', medium: 'organic' },
  { prefix: 'bing', source: 'bing', medium: 'organic' },
  { prefix: 'yahoo', source: 'yahoo', medium: 'organic' },
  { prefix: 'duckduckgo', source: 'duckduckgo', medium: 'organic' },
  { prefix: 'ecosia', source: 'ecosia', medium: 'organic' },
  { prefix: 'linkedin', source: 'linkedin', medium: 'social' },
  { exact: 'lnkd.in', source: 'linkedin', medium: 'social' },
  { prefix: 'twitter', source: 'twitter', medium: 'social' },
  { exact: 'x.com', source: 'twitter', medium: 'social' },
  { exact: 't.co', source: 'twitter', medium: 'social' },
  { prefix: 'facebook', source: 'facebook', medium: 'social' },
  { exact: 'fb.com', source: 'facebook', medium: 'social' },
  { prefix: 'instagram', source: 'instagram', medium: 'social' },
  { prefix: 'youtube', source: 'youtube', medium: 'social' },
  { prefix: 'reddit', source: 'reddit', medium: 'social' }
];

const matchReferrer = host => {
  const labels = host.split('.');
  return REFERRER_SOURCE_MAP.find(entry => {
    if (entry.exact) return host === entry.exact || host === 'www.' + entry.exact;
    if (entry.prefix) return labels.includes(entry.prefix);
    return false;
  });
};

const isBrowser = () => typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined';

/**
 * Parse tracked params from a URL search string.
 *
 * @param {string} search - e.g. window.location.search
 * @return {object} map of tracked keys present in the URL
 */
const parseTrackedParams = search => {
  if (!search) return {};
  const params = new URLSearchParams(search);
  const found = {};
  TRACKED_KEYS.forEach(key => {
    const value = params.get(key);
    if (value) found[key] = value;
  });
  return found;
};

/**
 * Derive { utm_source, utm_medium } from document.referrer for visits that
 * arrive without explicit UTMs.
 */
const deriveFromReferrer = () => {
  if (typeof document === 'undefined') return {};
  const referrer = document.referrer;

  // No referrer → direct / typed URL / bookmark.
  if (!referrer) return { utm_source: 'direct', utm_medium: 'none' };

  let host = '';
  try {
    host = new URL(referrer).hostname.toLowerCase();
  } catch {
    return { utm_source: 'direct', utm_medium: 'none' };
  }

  // Internal navigation shouldn't overwrite attribution.
  if (typeof window !== 'undefined' && host === window.location.hostname) return {};

  const known = matchReferrer(host);
  if (known) return { utm_source: known.source, utm_medium: known.medium };

  return { utm_source: host.replace(/^www\./, ''), utm_medium: 'referral' };
};

/**
 * Read UTM values previously stored in sessionStorage.
 *
 * @return {object} map of tracked keys (empty object if none / SSR)
 */
export const getStoredUtmParams = () => {
  if (!isBrowser()) return {};
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

/**
 * Capture UTM params from the current URL and merge them into sessionStorage.
 * When the URL has no UTMs and none have been stored yet, values are derived
 * from document.referrer so direct / organic visits still carry attribution.
 * Explicit URL params always win over stored / derived values.
 */
export const captureUtmParamsFromLocation = () => {
  if (!isBrowser()) return;
  const fromUrl = parseTrackedParams(window.location.search);
  const existing = getStoredUtmParams();

  let toPersist = { ...existing, ...fromUrl };

  // Only seed referrer-based attribution once per session.
  if (Object.keys(existing).length === 0 && Object.keys(fromUrl).length === 0) {
    toPersist = deriveFromReferrer();
  }

  if (Object.keys(toPersist).length === 0) return;

  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(toPersist));
  } catch {
    // sessionStorage may be unavailable (private mode / quota). Silently ignore.
  }
};
